import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import { ChevronDownIcon, ChevronRightIcon, CheckIcon } from '@heroicons/react/24/outline';

interface PromotionCode {
  id: string;
  code: string;
  active: boolean;
  max_redemptions: number | null;
  times_redeemed: number;
  expires_at: string | null;
  created_at: string;
  metadata: any;
  notes?: string;
  assigned_to?: string;
  created_by?: string;
}

interface Coupon {
  id: string;
  name: string;
  percent_off: number | null;
  amount_off: number | null;
  duration: string;
  duration_in_months: number | null;
  valid: boolean;
  redeem_by: string | null;
  max_redemptions: number | null;
  times_redeemed: number;
  created_at: string;
  metadata: any;
  promotion_codes: PromotionCode[];
  promotion_codes_count: number;
  total_redemptions: number;
}

interface Summary {
  total_coupons: number;
  total_promotion_codes: number;
  total_redemptions: number;
  stripe_mode: 'LIVE' | 'TEST';
}

interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  metadata: any;
  prices: Array<{
    id: string;
    unit_amount: number | null;
    currency: string;
    recurring: any;
    nickname: string | null;
    active: boolean;
  }>;
}

export default function CouponManagement() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [expandedCoupons, setExpandedCoupons] = useState<Set<string>>(new Set());
  const [showCreateCouponForm, setShowCreateCouponForm] = useState(false);
  const [showCreatePromoForm, setShowCreatePromoForm] = useState<string | null>(null);
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [showInactiveCoupons, setShowInactiveCoupons] = useState(false);
  
  const [newCoupon, setNewCoupon] = useState({
    id: '',
    name: '',
    percent_off: 100,
    duration: 'repeating',
    duration_in_months: 3,
    applies_to_products: [] as string[],
  });

  const [newPromoCode, setNewPromoCode] = useState({
    code: '',
    max_redemptions: 1,
    expires_at: '',
    notes: '',
  });

  useEffect(() => {
    fetchCoupons();
    fetchProducts();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/stripe-coupons', {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCoupons(data.coupons || []);
        setSummary(data.summary || null);
      } else {
        console.error('Failed to fetch coupons');
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/admin/stripe-products', {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      } else {
        console.error('Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const createCoupon = async () => {
    try {
      console.log('Creating coupon with data:', newCoupon);
      
      const response = await fetch('/api/admin/stripe-coupons/manage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          action: 'create',
          coupon_data: newCoupon
        })
      });

      if (response.ok) {
        await fetchCoupons();
        setShowCreateCouponForm(false);
        setNewCoupon({
          id: '',
          name: '',
          percent_off: 100,
          duration: 'repeating',
          duration_in_months: 3,
          applies_to_products: [],
        });
      } else {
        const error = await response.json();
        console.error('Create coupon error:', error);
        alert(`Failed to create coupon: ${error.details || error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating coupon:', error);
      alert('Failed to create coupon');
    }
  };

  const createPromoCode = async (couponId: string) => {
    try {
      const response = await fetch('/api/admin/promo-codes/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          ...newPromoCode,
          coupon_id: couponId,
        })
      });

      if (response.ok) {
        await fetchCoupons();
        setShowCreatePromoForm(null);
        setNewPromoCode({
          code: '',
          max_redemptions: 1,
          expires_at: '',
          notes: '',
        });
      } else {
        const error = await response.json();
        alert(`Failed to create promo code: ${error.details || error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating promo code:', error);
      alert('Failed to create promo code');
    }
  };

  const toggleCouponExpansion = (couponId: string) => {
    const newExpanded = new Set(expandedCoupons);
    if (newExpanded.has(couponId)) {
      newExpanded.delete(couponId);
    } else {
      newExpanded.add(couponId);
    }
    setExpandedCoupons(newExpanded);
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header with summary */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Stripe Coupon Management</h2>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <span className="text-sm font-medium">Show inactive coupons</span>
              <button
                type="button"
                role="switch"
                aria-checked={showInactiveCoupons}
                onClick={() => setShowInactiveCoupons(!showInactiveCoupons)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  showInactiveCoupons ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showInactiveCoupons ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              summary?.stripe_mode === 'LIVE'
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {summary?.stripe_mode || 'TEST'} Mode
            </span>
            <button
              onClick={() => setShowCreateCouponForm(!showCreateCouponForm)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create New Coupon
            </button>
          </div>
        </div>
        
        {summary && (
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm text-gray-500">Active Coupons</div>
              <div className="text-2xl font-semibold">{summary.total_coupons}</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm text-gray-500">Promotion Codes</div>
              <div className="text-2xl font-semibold">{summary.total_promotion_codes}</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm text-gray-500">Total Redemptions</div>
              <div className="text-2xl font-semibold">{summary.total_redemptions}</div>
            </div>
          </div>
        )}
      </div>

      {/* Create Coupon Form */}
      {showCreateCouponForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium mb-4">Create New Base Coupon</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={newCoupon.name}
                onChange={(e) => setNewCoupon({ ...newCoupon, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="e.g., Holiday Sale - 50% Off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coupon ID (optional)
              </label>
              <input
                type="text"
                value={newCoupon.id}
                onChange={(e) => setNewCoupon({ ...newCoupon, id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="e.g., HOLIDAY50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount %
              </label>
              <input
                type="number"
                value={newCoupon.percent_off}
                onChange={(e) => setNewCoupon({ ...newCoupon, percent_off: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration
              </label>
              <select
                value={newCoupon.duration}
                onChange={(e) => setNewCoupon({ ...newCoupon, duration: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="forever">Forever</option>
                <option value="once">Once</option>
                <option value="repeating">Repeating</option>
              </select>
            </div>
            {newCoupon.duration === 'repeating' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (months)
                </label>
                <input
                  type="number"
                  value={newCoupon.duration_in_months || ''}
                  onChange={(e) => setNewCoupon({ ...newCoupon, duration_in_months: e.target.value ? parseInt(e.target.value) : 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            )}
            <div className="col-span-2">
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  className="mr-2"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setNewCoupon({ ...newCoupon, applies_to_products: [] });
                    } else {
                      setNewCoupon({ ...newCoupon, applies_to_products: ['all'] });
                    }
                  }}
                  checked={!newCoupon.applies_to_products.includes('all')}
                />
                <span className="text-sm font-medium text-gray-700">Apply to specific products</span>
              </label>
              
              {!newCoupon.applies_to_products.includes('all') && (
                <div className="space-y-3">
                  {/* Selected Products */}
                  {newCoupon.applies_to_products.filter(id => id !== 'all').map((productId) => {
                    const product = products.find(p => p.id === productId);
                    if (!product) return null;
                    const priceInfo = product.prices.length > 0 
                      ? `$${product.prices[0].unit_amount ? (product.prices[0].unit_amount / 100).toFixed(2) : '0.00'}${product.prices[0].recurring ? `/${product.prices[0].recurring.interval}` : ''}`
                      : '';
                    return (
                      <div key={productId} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                        <div>
                          <div className="font-medium text-sm">{product.name}</div>
                          <div className="text-xs text-gray-500">{priceInfo}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setNewCoupon({
                              ...newCoupon,
                              applies_to_products: newCoupon.applies_to_products.filter(id => id !== productId)
                            });
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                  
                  {/* Product Dropdown */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Find a product..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      onFocus={() => setShowProductDropdown(true)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    
                    {showProductDropdown && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {products
                          .filter(product => 
                            !newCoupon.applies_to_products.includes(product.id) &&
                            product.name.toLowerCase().includes(productSearch.toLowerCase())
                          )
                          .map((product) => {
                            const priceInfo = product.prices.length > 0 
                              ? `$${product.prices[0].unit_amount ? (product.prices[0].unit_amount / 100).toFixed(2) : '0.00'}${product.prices[0].recurring ? `/${product.prices[0].recurring.interval}` : ''}`
                              : '';
                            return (
                              <button
                                key={product.id}
                                type="button"
                                onClick={() => {
                                  setNewCoupon({
                                    ...newCoupon,
                                    applies_to_products: [...newCoupon.applies_to_products.filter(id => id !== 'all'), product.id]
                                  });
                                  setProductSearch('');
                                  setShowProductDropdown(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-sm">{product.name}</div>
                                <div className="text-xs text-gray-500">{priceInfo}</div>
                              </button>
                            );
                          })}
                      </div>
                    )}
                  </div>
                  
                  {/* Click outside to close dropdown */}
                  {showProductDropdown && (
                    <div 
                      className="fixed inset-0 z-0" 
                      onClick={() => setShowProductDropdown(false)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={createCoupon}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Create Coupon
            </button>
            <button
              onClick={() => setShowCreateCouponForm(false)}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Coupons List */}
      <div className="space-y-4">
        {coupons.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No active coupons found.
          </div>
        ) : (
          <>
            {(() => {
              const filteredCoupons = coupons.filter(coupon => showInactiveCoupons || coupon.valid);
              const inactiveCount = coupons.filter(c => !c.valid).length;
              return (
                <>
                  {!showInactiveCoupons && inactiveCount > 0 && (
                    <div className="text-sm text-gray-500 mb-2">
                      Hiding {inactiveCount} inactive coupon{inactiveCount !== 1 ? 's' : ''}
                    </div>
                  )}
                  {filteredCoupons.map((coupon) => (
            <div key={coupon.id} className="bg-white rounded-lg shadow">
              {/* Coupon Header */}
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleCouponExpansion(coupon.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {expandedCoupons.has(coupon.id) ? (
                      <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">{coupon.name}</h3>
                        {!coupon.valid && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {coupon.percent_off}% off • {coupon.duration}
                        {coupon.duration === 'repeating' && ` (${coupon.duration_in_months} months)`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {coupon.promotion_codes_count} promo codes
                      </div>
                      <div className="text-sm text-gray-500">
                        {coupon.total_redemptions} redemptions
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCreatePromoForm(coupon.id);
                        // Prepopulate with coupon ID and hyphen
                        setNewPromoCode({
                          ...newPromoCode,
                          code: `${coupon.id}-`
                        });
                      }}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Add Promo Code
                    </button>
                  </div>
                </div>
              </div>

              {/* Create Promo Code Form */}
              {showCreatePromoForm === coupon.id && (
                <div className="px-4 pb-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium mb-3">Create Promotion Code</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Code
                        </label>
                        <input
                          type="text"
                          value={newPromoCode.code}
                          onChange={(e) => setNewPromoCode({ ...newPromoCode, code: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="e.g., SAVE20NOW"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Max Uses
                        </label>
                        <input
                          type="number"
                          value={newPromoCode.max_redemptions}
                          onChange={(e) => setNewPromoCode({ ...newPromoCode, max_redemptions: parseInt(e.target.value) })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Expires
                        </label>
                        <input
                          type="date"
                          value={newPromoCode.expires_at}
                          onChange={(e) => setNewPromoCode({ ...newPromoCode, expires_at: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Notes
                        </label>
                        <input
                          type="text"
                          value={newPromoCode.notes}
                          onChange={(e) => setNewPromoCode({ ...newPromoCode, notes: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="Assigned to..."
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => createPromoCode(coupon.id)}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Create
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowCreatePromoForm(null);
                          // Reset promo code form
                          setNewPromoCode({
                            code: '',
                            max_redemptions: 1,
                            expires_at: '',
                            notes: '',
                          });
                        }}
                        className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Expanded Promo Codes */}
              {expandedCoupons.has(coupon.id) && coupon.promotion_codes.length > 0 && (
                <div className="border-t">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Uses</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {coupon.promotion_codes.map((promo) => (
                        <tr key={promo.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{promo.code}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{promo.assigned_to || promo.notes || '-'}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {promo.times_redeemed} / {promo.max_redemptions || '∞'}
                          </td>
                          <td className="px-4 py-2">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              promo.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {promo.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {new Date(promo.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
                  ))}
                </>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}